/**
 * ログイン後に戻る画面の引き継ぎ
 * スマートフォンのカメラやLINEでQRを読むと、アプリを使っているのとは別のブラウザで開くことがあります。
 * そのブラウザにログイン情報が無くてもQRの値を失わず、ログイン・新規登録の後にQRの続きへ戻すために使います。
 */

import { VISITOR_ROUTES } from '../../../navigation/routeNames';

/** ログイン後に戻る画面 */
export type LoginReturnDestination = {
  /** 戻る画面のルート名 */
  name: string;
  /** 戻る画面へ渡すQRの値 */
  params: Record<string, string>;
};

/** ログイン後に戻れる画面と、その画面のQRが持つ値の名前 */
const LOGIN_RETURN_ROUTE_PARAM_NAMES: Readonly<Record<string, readonly string[]>> = Object.freeze({
  [VISITOR_ROUTES.SCAN]: Object.freeze(['token', 'claimToken', 'code', 't']),
  [VISITOR_ROUTES.GROUP]: Object.freeze(['code']),
});

/**
 * QRで開いた画面を、ログイン後に戻る画面として作る
 * QRの値だけを持ち、認証値など他の値は引き継がない。
 * @param {unknown} routeName - 開いた画面のルート名
 * @param {unknown} params - 開いた画面のパラメータ
 * @returns {LoginReturnDestination | null} 戻る画面。QRで開いた画面でなければnull
 */
export const createLoginReturnDestination = (
  routeName: unknown,
  params: unknown,
): LoginReturnDestination | null => {
  if (
    typeof routeName !== 'string'
    || !Object.prototype.hasOwnProperty.call(LOGIN_RETURN_ROUTE_PARAM_NAMES, routeName)
    || !params
    || typeof params !== 'object'
  ) {
    return null;
  }

  /** 引き継ぐQRの値 */
  const carriedParams: Record<string, string> = {};

  for (const paramName of LOGIN_RETURN_ROUTE_PARAM_NAMES[routeName]) {
    /** パラメータの値 */
    const value = (params as Record<string, unknown>)[paramName];
    /** 前後空白を除いた値 */
    const trimmedValue = typeof value === 'string' ? value.trim() : '';

    if (trimmedValue) {
      carriedParams[paramName] = trimmedValue;
    }
  }

  return Object.keys(carriedParams).length > 0
    ? { name: routeName, params: carriedParams }
    : null;
};

/**
 * 画面間で受け渡した戻り先を検証し直す
 * @param {unknown} value - ログイン・新規登録画面が受け取った戻り先
 * @returns {LoginReturnDestination | null} 戻る画面。妥当でなければnull
 */
export const resolveLoginReturnDestination = (value: unknown): LoginReturnDestination | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  /** 受け取った戻り先 */
  const destination = value as { name?: unknown; params?: unknown };

  return createLoginReturnDestination(destination.name, destination.params);
};
